import type { MailboxGrantDto, MemberDto } from "@bafe/api-client";
import { Button, Card, InlineNotice, Section, useToast } from "@bafe/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useId, useState } from "react";

import { ConfirmDialog } from "../../components/ConfirmDialog";
import { HubSelect } from "../../components/HubSelect";
import { Popup } from "../../components/Popup";
import { ErrorPanel } from "../../components/ErrorPanel";
import { QueryState } from "../../components/QueryState";
import { api } from "../../lib/api";
import { queryKeys } from "../../lib/query";

const ACCESS_LABEL: Record<MailboxGrantDto["access"], string> = {
	owner: "Owner",
	full: "Can send as this address",
	read_only: "See only",
};

export function MailboxAccess({ mailboxId }: { mailboxId: string }) {
	const grants = useQuery({
		queryKey: queryKeys.mailboxGrants(mailboxId),
		queryFn: () => api.listMailboxGrants(mailboxId),
		enabled: mailboxId !== "",
	});

	return (
		<QueryState query={grants} errorTitle="We could not load who can open this inbox.">
			{(rows) => <AccessPanel mailboxId={mailboxId} grants={rows} />}
		</QueryState>
	);
}

function AccessPanel({ mailboxId, grants }: { mailboxId: string; grants: MailboxGrantDto[] }) {
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const sidebarFieldId = useId();
	const members = useQuery({
		queryKey: queryKeys.members,
		queryFn: () => api.listMembers(),
	});
	const [open, setOpen] = useState(false);
	const [userId, setUserId] = useState("");
	const [access, setAccess] = useState<"full" | "read_only">("read_only");
	const [showInSidebar, setShowInSidebar] = useState(false);
	const [removing, setRemoving] = useState<MailboxGrantDto | null>(null);

	const granted = new Set(grants.map((grant) => grant.userId));
	const candidates = (members.data ?? []).filter(
		(member) => member.status === "active" && !granted.has(member.userId),
	);
	const chosenId = userId || candidates[0]?.userId || "";

	const refresh = () => {
		void queryClient.invalidateQueries({ queryKey: queryKeys.mailboxGrants(mailboxId) });
	};

	const assign = useMutation({
		mutationFn: () =>
			api.assignMailboxAccess(mailboxId, { userId: chosenId, access, showInSidebar }),
		onSuccess: () => {
			setUserId("");
			setShowInSidebar(false);
			setOpen(false);
			refresh();
			toast({
				tone: "success",
				message: showInSidebar
					? "They can open this inbox and it will appear in their webmail sidebar."
					: access === "full"
						? "They can send as this address."
						: "They can see this inbox.",
			});
		},
	});

	const sidebar = useMutation({
		mutationFn: (grant: MailboxGrantDto) =>
			api.updateMailboxGrantSidebar(mailboxId, grant.userId, !grant.showInSidebar),
		onSuccess: (_result, grant) => {
			refresh();
			toast({
				tone: "success",
				message: grant.showInSidebar
					? "Removed from their webmail sidebar."
					: "Added to their webmail sidebar.",
			});
		},
	});

	const remove = useMutation({
		mutationFn: (grant: MailboxGrantDto) => api.removeMailboxAccess(mailboxId, grant.userId),
		onSuccess: () => {
			setRemoving(null);
			refresh();
			toast({ tone: "success", message: "Access removed." });
		},
	});

	const submit = (event: FormEvent) => {
		event.preventDefault();
		if (!chosenId || assign.isPending) return;
		assign.mutate();
	};

	function close() {
		if (assign.isPending) return;
		setOpen(false);
	}

	return (
		<div className="flex flex-col gap-8">
			<div className="row head-actions">
				<p className="sub">
					Access lets someone open this inbox. Sidebar visibility is separate and only affects what appears in
					webmail.
				</p>
				{candidates.length > 0 && (
					<button className="btn" type="button" onClick={() => setOpen(true)}>
						Give access
					</button>
				)}
			</div>

			<Section title="People who can open this inbox">
				<ul className="flex flex-col gap-2">
					{grants.map((grant) => (
						<li key={grant.id}>
							<Card className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
								<div className="min-w-0">
									<p className="truncate text-sm font-medium text-(--text-primary)">{grant.fullName}</p>
									<p className="truncate text-sm text-(--text-secondary)">{grant.email}</p>
								</div>
								<div className="flex shrink-0 flex-wrap items-center gap-2">
									<span className="text-sm text-(--text-secondary)">{ACCESS_LABEL[grant.access]}</span>
									<label className="check text-sm">
										<input
											type="checkbox"
											checked={grant.showInSidebar}
											disabled={sidebar.isPending}
											onChange={() => sidebar.mutate(grant)}
										/>
										<span>Webmail sidebar</span>
									</label>
									{grant.access !== "owner" && (
										<Button size="sm" variant="ghost" onClick={() => setRemoving(grant)}>
											Remove<span className="sr-only"> {grant.fullName}</span>
										</Button>
									)}
								</div>
							</Card>
						</li>
					))}
				</ul>
			</Section>

			{members.isError ? (
				<ErrorPanel
					error={members.error}
					title="We could not load your team."
					onRetry={() => void members.refetch()}
				/>
			) : candidates.length === 0 ? (
				<InlineNotice tone="info">
					Invite someone to the organisation first. People who already have access are not listed again.
				</InlineNotice>
			) : null}

			<Popup open={open} title="Give access" onClose={close}>
				<form onSubmit={submit} noValidate>
					<p className="sub">Only people in this organisation can be added. They cannot add themselves.</p>
					{assign.isError && <p className="err">We could not give that person access.</p>}
					<label>Person</label>
					<HubSelect
						id="mailbox-access-person"
						label="Person"
						value={chosenId}
						items={candidates.map((member) => ({
							value: member.userId,
							label: memberLabel(member),
						}))}
						onChange={setUserId}
					/>
					<label>What they can do</label>
					<HubSelect
						id="mailbox-access-level"
						label="What they can do"
						value={access}
						items={[
							{ value: "read_only", label: "See only" },
							{ value: "full", label: "Can send as this address" },
						]}
						onChange={(next) => setAccess(next as "full" | "read_only")}
					/>
					<label className="check" htmlFor={sidebarFieldId}>
						<input
							id={sidebarFieldId}
							type="checkbox"
							checked={showInSidebar}
							onChange={(event) => setShowInSidebar(event.target.checked)}
						/>
						<span>Show in webmail sidebar</span>
					</label>
					<p className="sub">
						They can still open this inbox from Manage when this is off. Unified inbox in webmail only includes
						addresses shown on the sidebar.
					</p>
					<p className="actions">
						<button className="btn" type="submit" disabled={!chosenId || assign.isPending}>
							{assign.isPending ? "Saving" : "Give access"}
						</button>
						<button className="btn ghost" type="button" onClick={close}>
							Cancel
						</button>
					</p>
				</form>
			</Popup>

			<ConfirmDialog
				open={removing !== null}
				onClose={() => setRemoving(null)}
				title="Remove access"
				description={
					removing
						? `${removing.fullName} will no longer open this inbox. Mail already in the inbox stays where it is.`
						: undefined
				}
				confirmLabel="Remove access"
				danger
				pending={remove.isPending}
				error={remove.error}
				onConfirm={() => {
					if (removing) remove.mutate(removing);
				}}
			/>
		</div>
	);
}

function memberLabel(member: MemberDto): string {
	return member.fullName ? `${member.fullName} (${member.email})` : member.email;
}
